/**
 * 运行轨迹抽屉 · ③ 详情栏（PR5 新写；线框 01 §三，ref 04 O-2～O-4、O-10，判定卡 03 差异 6～8）。
 * 点中栏一项才出；详情类型之间用分段控件切换（对象级），分段里只出现点中那一项有的类型。
 * 完整输入输出走 A-25（PR4 起树里只带摘要）；A-25 还没上线（回 404）时退回用树里的 attributes。
 * 计划版本对比走 A-24：首个版本左「计划器原稿」右「校验后 v1」，标出剔除 ／ 修正。
 */
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { AiMark } from "@/components/ui/ai-card";
import { Badge } from "@/components/ui/badge";
import { CodeBlock, JsonBlock } from "@/components/ui/code-block";
import { LoadingInline } from "@/components/ui/feedback";
import {
	type PlanEvent,
	type PlanItemView,
	type PlanRecordDetail,
	type PlanVersionRecord,
	isNotFound,
	planErrorMessage,
	usePlanEvent,
	usePlanVersions,
} from "@/lib/api/plan-lab";
import { cn } from "@/lib/utils";
import { type DemandLabel, useDemandLabel } from "../kit/demand-label";
import { FailState } from "../kit/fail-state";
import {
	CANDIDATE_ROLE_ZH,
	CHECK_VERDICT_ZH,
	GUARD_VERDICT_TONE,
	GUARD_VERDICT_ZH,
	GUARD_ZH,
	ITEM_STATUS_ZH,
	N2_ACTION_ZH,
	PLAN_CHANGE_TONE,
	PLAN_CHANGE_ZH,
	PLAN_GENERATOR_ZH,
	REPLY_TYPE_ZH,
	RESUME_ACTION_ZH,
	TOOL_BACKEND_ZH,
	WAIT_KIND_ZH,
	WRITE_CLASS_ZH,
	activationText,
	zh,
} from "../kit/plan-labels";
import { type PlanColumn, PlanTable } from "../kit/plan-table";
import { Segmented } from "../kit/segmented";
import { CustomerCard, PanelHead } from "./execution-drawer";
import type { TraceSel } from "./trace-column";
import {
	type TraceGroup,
	type TraceModel,
	type TraceRow,
	attrs,
	chatNode,
	fmtMs,
	mergedList,
	objectText,
	op,
	parseJson,
	sideCheckWarns,
} from "./trace-model";

type Seg =
	| "io"
	| "reason"
	| "signal"
	| "compare"
	| "items"
	| "checks"
	| "item"
	| "candidates"
	| "guard"
	| "stop"
	| "called"
	| "side"
	| "recall"
	| "bg"
	| "raw";

const SEG_LABEL: Record<Seg, string> = {
	io: "输入输出",
	reason: "决策理由",
	signal: "信号",
	compare: "版本对比",
	items: "事项清单",
	checks: "计划校验",
	item: "事项",
	candidates: "候选表",
	guard: "护栏明细",
	stop: "判停",
	called: "调用了 vs 用进答案",
	side: "旁检",
	recall: "候选表",
	bg: "客户背景",
	raw: "明细",
};

type Target =
	| { kind: "group"; g: TraceGroup }
	| { kind: "row"; r: TraceRow; g?: TraceGroup }
	| { kind: "bg" }
	| null;

function resolve(sel: TraceSel, model: TraceModel | null): Target {
	if (!sel) return null;
	if (sel.kind === "bg") return { kind: "bg" };
	if (!model) return null;
	if (sel.kind === "group") {
		const g = model.groups.find((x) => x.key === sel.key);
		return g ? { kind: "group", g } : null;
	}
	const g = model.groups.find((x) => x.key === sel.groupKey);
	const r = (g?.rows ?? model.flat).find((x) => x.id === sel.id);
	return r ? { kind: "row", r, g } : null;
}

/** 分段里只出现点中那一项有的类型（线框 §三 🟡 按建议） */
function segmentsOf(t: Target): Seg[] {
	if (!t) return [];
	if (t.kind === "bg") return ["bg"];
	if (t.kind === "group") {
		const g = t.g;
		if (g.kind === "signal") return ["signal"];
		if (g.kind === "recall") return ["recall"];
		if (g.kind === "planning")
			return g.rows.some((r) => r.kind === "checks")
				? ["compare", "items", "checks"]
				: ["compare", "items"];
		if (g.kind === "item") return ["item", "candidates"];
		if (g.kind === "generation") return ["called", "io", "side"];
		return ["raw"];
	}
	const r = t.r;
	switch (r.kind) {
		case "signal":
			return ["signal"];
		case "n2":
			return ["io", "reason"];
		case "recall":
			return ["recall"];
		case "guard":
			return ["guard"];
		case "tool":
			return ["io"];
		case "stop":
			return ["stop"];
		case "checks":
			return ["checks", "compare"];
		case "planning":
			return ["compare", "items"];
		case "n3":
			return ["io", "called"];
		case "side_check":
		case "speakable":
			return ["side"];
		default:
			return ["raw"];
	}
}

export function TraceDetail({
	sel,
	model,
	detail,
	customerId,
}: {
	sel: TraceSel;
	model: TraceModel | null;
	detail: PlanRecordDetail | null;
	customerId?: string;
}) {
	const t = resolve(sel, model);
	const segs = segmentsOf(t);
	const [seg, setSeg] = useState<Seg | null>(null);
	const selKey = sel ? JSON.stringify(sel) : "";
	// biome-ignore lint/correctness/useExhaustiveDependencies: 换了点中项就回到第一个分段
	useEffect(() => setSeg(null), [selKey]);
	// 换了点中项 ／ 分段就回到顶部（不然短内容会落在上一段的滚动位置下面，看着像空白）
	const scroller = useRef<HTMLDivElement | null>(null);
	// biome-ignore lint/correctness/useExhaustiveDependencies: 只在点中项或分段变化时回顶
	useEffect(() => {
		if (scroller.current) scroller.current.scrollTop = 0;
	}, [selKey, seg]);
	const cur: Seg | null = seg && segs.includes(seg) ? seg : (segs[0] ?? null);

	let title = "详情";
	let body: ReactNode;
	if (!t || !cur) {
		body = (
			<div className="rounded-lg border border-dashed border-surface-border p-6 text-center text-sm text-text-muted">
				点中间一项看详情
			</div>
		);
	} else {
		title =
			t.kind === "bg"
				? "客户背景"
				: t.kind === "group"
					? `${t.g.kind === "item" ? "事项" : t.g.title} · ${t.g.summary}`
					: `${t.r.name} · ${t.r.text}`;
		body = (
			<SegBody
				seg={cur}
				t={t}
				model={model as TraceModel}
				detail={detail}
				customerId={customerId}
			/>
		);
	}
	return (
		<div className="flex h-full min-h-0 flex-col" data-testid="plan-col-detail">
			<div ref={scroller} className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
				<PanelHead title={title} />
				{segs.length > 1 && cur ? (
					<Segmented
						testId="plan-detail-seg"
						options={segs.map((s) => ({ value: s, label: SEG_LABEL[s] }))}
						value={cur}
						onChange={setSeg}
					/>
				) : null}
				{body}
			</div>
		</div>
	);
}

function SegBody({
	seg,
	t,
	model,
	detail,
	customerId,
}: {
	seg: Seg;
	t: NonNullable<Target>;
	model: TraceModel;
	detail: PlanRecordDetail | null;
	customerId?: string;
}) {
	const row = t.kind === "row" ? t.r : null;
	const group = t.kind === "group" ? t.g : t.kind === "row" ? t.g : undefined;
	const tree = detail?.tree ?? [];
	switch (seg) {
		case "bg":
			return <CustomerCard customerId={customerId} />;
		case "io": {
			const ev =
				row?.ev ??
				(group?.kind === "generation" ? group.rows.find((r) => r.kind === "n3")?.ev : undefined);
			return ev ? <EventIO ev={ev} /> : <Muted>这一项没有模型调用。</Muted>;
		}
		case "reason":
			return row ? <N2Reason row={row} /> : null;
		case "signal": {
			const ev = row?.kind === "signal" ? row.ev : group?.rows.find((r) => r.kind === "signal")?.ev;
			return <SignalDetail ev={ev} />;
		}
		case "compare":
			return <PlanCompare model={model} detail={detail} />;
		case "items":
			return <ItemList items={detail?.items_now ?? []} />;
		case "checks": {
			const checks = row?.checks ?? group?.rows.find((r) => r.kind === "checks")?.checks ?? [];
			return <ChecksTable checks={checks} />;
		}
		case "item":
			return group ? <ItemDetail g={group} tree={tree} /> : null;
		case "candidates":
			return group?.item ? (
				<CandidateTable itemId={group.item.item_id} model={model} detail={detail} />
			) : (
				<Muted>没有事项信息。</Muted>
			);
		case "guard":
			return row?.ev ? <GuardDetail ev={row.ev} /> : null;
		case "stop":
			return row?.ev ? <StopDetail ev={row.ev} tree={tree} /> : null;
		case "called":
			return <CalledVsUsed tree={tree} detail={detail} />;
		case "side":
			return <SideDetail tree={tree} />;
		case "recall": {
			const ev =
				(row?.kind === "recall" ? row.ev : undefined) ??
				model.flat.find((r) => r.kind === "recall")?.ev;
			return ev ? (
				<RecallDetail ev={ev} />
			) : (
				<Muted>本轮没有召回：本轮不是决策点（不是首轮、也没有新诉求）。</Muted>
			);
		}
		default:
			return row?.ev ? <JsonBlock value={attrs(row.ev)} /> : <Muted>—</Muted>;
	}
}

function Muted({ children }: { children: ReactNode }) {
	return <div className="text-sm text-text-muted">{children}</div>;
}

function Label({ children, ai }: { children: ReactNode; ai?: boolean }) {
	return (
		<div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-text-secondary">
			{ai ? <AiMark /> : null}
			{children}
		</div>
	);
}

function textOf(v: unknown): string | null {
	if (v === null || v === undefined || v === "") return null;
	if (typeof v === "string") return v;
	if (typeof v === "object" && v && "text" in v) return String((v as { text: unknown }).text ?? "");
	return JSON.stringify(v, null, 2);
}

/** 键值表（统一表格件，两列） */
function KvTable({ rows, testId }: { rows: [string, ReactNode][]; testId?: string }) {
	const cols: PlanColumn<[string, ReactNode]>[] = [
		{ key: "k", title: "项", className: "w-28 text-text-secondary", render: (r) => r[0] },
		{ key: "v", title: "值", className: "whitespace-normal", render: (r) => r[1] },
	];
	return (
		<div data-testid={testId}>
			<PlanTable columns={cols} rows={rows} rowKey={(r) => r[0]} density="compact" />
		</div>
	);
}

/** 一个事件的完整输入输出（A-25；没上线时用树里的 attributes） */
function EventIO({ ev }: { ev: PlanEvent }) {
	const q = usePlanEvent(ev.span_id);
	if (q.isLoading) return <LoadingInline text="取完整输入输出…" />;
	let a: Record<string, unknown>;
	if (q.data) a = q.data;
	else if (q.isError && isNotFound(q.error) && !ev.attributes_summarized) a = attrs(ev);
	else
		return (
			<FailState
				testId="plan-detail-fail"
				title="完整输入输出没取到"
				reason={planErrorMessage(q.error)}
				onRetry={() => q.refetch()}
				retrying={q.isFetching}
			/>
		);
	const meta = (
		<div className="flex flex-wrap items-center gap-1.5 text-xs text-text-secondary">
			<Badge tone="muted">耗时 {fmtMs(Number(a.ms ?? a.latency_ms ?? 0))}</Badge>
			{ev["gen_ai.request.model"] ? (
				<Badge tone="neutral">{String(ev["gen_ai.request.model"])}</Badge>
			) : null}
			{ev["gen_ai.usage.input_tokens"] !== undefined ? (
				<Badge tone="muted">
					token 入 {Number(ev["gen_ai.usage.input_tokens"] ?? 0)} · 出{" "}
					{Number(ev["gen_ai.usage.output_tokens"] ?? 0)}
				</Badge>
			) : null}
			{ev["plan.cost_usd"] ? (
				<Badge tone="muted">${Number(ev["plan.cost_usd"]).toFixed(5)}</Badge>
			) : null}
			{ev["plan.item_id"] ? <Badge tone="info">属于 {ev["plan.item_id"]}</Badge> : null}
		</div>
	);
	if (op(ev) === "chat") {
		const inp = (a.input ?? {}) as Record<string, unknown>;
		const out = (a.output ?? {}) as Record<string, unknown>;
		const parsed = parseJson(out.raw);
		const sys = textOf(inp.system);
		const user = textOf(inp.user);
		const raw = textOf(out.raw);
		return (
			<div className="space-y-3" data-testid="plan-detail-io">
				{meta}
				{sys ? (
					<div>
						<Label>system（固定指令）</Label>
						<CodeBlock code={sys} maxHeight={200} />
					</div>
				) : null}
				{user ? (
					<div>
						<Label>user（输入变量 ＋ 当前句）</Label>
						<CodeBlock code={user} maxHeight={240} />
					</div>
				) : null}
				{inp.vars ? (
					<div>
						<Label>输入变量</Label>
						<JsonBlock value={inp.vars} maxHeight={240} />
					</div>
				) : null}
				{raw ? (
					<div>
						<Label ai>模型原始响应</Label>
						<CodeBlock code={raw} maxHeight={240} />
					</div>
				) : null}
				{parsed ? (
					<div>
						<Label ai>解析结果</Label>
						<JsonBlock value={parsed} maxHeight={240} />
					</div>
				) : null}
			</div>
		);
	}
	if (op(ev) === "execute_tool") {
		const backend = String(a.backend ?? "");
		return (
			<div className="space-y-3" data-testid="plan-detail-io">
				{meta}
				<div className="flex flex-wrap items-center gap-2 text-xs">
					<span className="text-text-muted">调用来源</span>
					<Badge tone={backend === "live" ? "success" : backend === "recorded" ? "info" : "muted"}>
						{zh(TOOL_BACKEND_ZH, backend || null)}
					</Badge>
					{a.write_class ? (
						<Badge tone="warning">{zh(WRITE_CLASS_ZH, String(a.write_class))}</Badge>
					) : null}
					<span className="text-text-muted">状态</span>
					<Badge tone="neutral">{String(a.status ?? "—")}</Badge>
				</div>
				<div>
					<Label>入参</Label>
					<JsonBlock value={a.params ?? {}} maxHeight={200} />
				</div>
				<div>
					<Label>完整返回</Label>
					<JsonBlock value={a.result ?? a.result_brief ?? {}} maxHeight={320} />
				</div>
			</div>
		);
	}
	return (
		<div className="space-y-3" data-testid="plan-detail-io">
			{meta}
			<JsonBlock value={a} />
		</div>
	);
}

/** N2 一步的决策理由（理由是模型写的 → AiMark，和采纳与否、就绪信息这些事实分开） */
function N2Reason({ row }: { row: TraceRow }) {
	// 树里的模型输出可能是摘要（截到 200 字），理由取 A-25 的全量；取不到用事项事件「step」里的决策
	const full = usePlanEvent(row.ev?.span_id ?? null);
	const out = ((full.data ?? (row.ev ? attrs(row.ev) : {})).output ?? {}) as { raw?: unknown };
	const parsed = parseJson(textOf(out.raw));
	const step = row.step ? attrs(row.step) : {};
	const dec = parsed ?? ((step.decision ?? {}) as Record<string, unknown>);
	const reason = String(dec.reason ?? dec.note ?? dec.why_customer ?? "");
	const cands = Array.isArray(step.candidates)
		? (step.candidates as Record<string, unknown>[])
		: [];
	return (
		<div className="space-y-3" data-testid="plan-detail-reason">
			<div className="rounded-lg border border-surface-border p-3">
				<Label ai>Agent（N2）的决定与理由</Label>
				<div className="text-sm text-text-primary">{reason || "（模型没写理由）"}</div>
				{full.isError && !isNotFound(full.error) ? (
					<div className="mt-1 text-xs text-text-muted">
						完整输出没取到（{planErrorMessage(full.error)}），理由取自留痕摘要
					</div>
				) : null}
			</div>
			<KvTable
				rows={[
					["动作", zh(N2_ACTION_ZH, String(dec.action ?? "") || null)],
					["工具 ／ 槽", String(dec.tool_id ?? dec.slot ?? "—")],
					[
						"是否采纳",
						step.accepted === false ? (
							<Badge key="v" tone="warning">
								未采纳
							</Badge>
						) : (
							<Badge key="v" tone="success">
								采纳
							</Badge>
						),
					],
					["结果", String(step.why ?? "—")],
					["属于事项", String(row.ev?.["plan.item_id"] ?? "—")],
				]}
			/>
			{cands.length ? (
				<div>
					<Label>本步交给 Agent 的候选（召回建议的角色 · 就绪信息；选哪个由 Agent 定）</Label>
					<div className="flex flex-wrap gap-1.5">
						{cands.map((c) => (
							<Badge
								key={String(c.tool_id)}
								tone={String(c.tool_id) === String(dec.tool_id ?? "") ? "success" : "muted"}
								className="font-mono"
							>
								{String(c.tool_id) === String(dec.tool_id ?? "") ? "Agent 选了 · " : ""}
								{String(c.tool_id)}
								{c.role ? ` · ${zh(CANDIDATE_ROLE_ZH, String(c.role))}` : ""} ·{" "}
								{String(c.readiness ?? "—")}
							</Badge>
						))}
					</div>
				</div>
			) : null}
		</div>
	);
}

const TIER_TONE: Record<string, "success" | "info" | "muted"> = {
	推荐: "success",
	可选: "info",
	不推荐: "muted",
};

/** 召回详情：每件诉求的全部候选（档位 ／ 为什么在这档 ／ 命中路或意图标签），选中没选中都列。召回只给建议，不做路由。 */
function RecallDetail({ ev }: { ev: PlanEvent }) {
	const a = attrs(ev);
	const demands = (Array.isArray(a.demands) ? a.demands : []) as Record<string, unknown>[];
	const guides = (Array.isArray(a.guides) ? a.guides : []) as string[];
	const notes = (Array.isArray(a.notes) ? a.notes : []) as string[];
	return (
		<div className="space-y-3" data-testid="plan-detail-recall">
			<div className="rounded-lg border border-surface-border bg-surface-subtle p-3 text-sm text-text-primary">
				召回只给候选和建议（档位、诉求结论）。选哪个工具、要不要追问、办不了还是转人工，
				<b>最终都由 Agent（N2）决定</b>
				；代码只做安全约束：准入拦截、前置依赖、写操作确认门。
			</div>
			{a.degraded ? <Muted>召回降级：{String(a.degraded)}，本轮不建事项。</Muted> : null}
			{demands.map((d) => {
				const cands = (Array.isArray(d.candidates) ? d.candidates : []) as Record<
					string,
					unknown
				>[];
				const blocked = (Array.isArray(d.admission_blocked) ? d.admission_blocked : []) as Record<
					string,
					unknown
				>[];
				return (
					<div key={String(d.demand_id)} className="space-y-1.5">
						<div className="flex flex-wrap items-center gap-1.5 text-sm">
							<span className="font-mono text-text-muted">{String(d.demand_id)}</span>
							<span className="text-text-primary">{String(d.goal ?? "")}</span>
							{d.intent ? <Badge tone="muted">标签 {String(d.intent)}</Badge> : null}
							<Badge tone="info">结论（提示）{String(d.conclusion ?? "—")}</Badge>
						</div>
						{cands.length ? (
							<KvTable
								rows={cands.map((c) => [
									String(c.tool_id),
									<span key={String(c.tool_id)} className="flex flex-wrap items-center gap-1.5">
										<Badge tone={TIER_TONE[String(c.tier)] ?? "muted"}>
											建议 {String(c.tier ?? "—")}
										</Badge>
										<span className="text-text-secondary">
											{/* 画像 pv4 起（相关性为主、意图加分）留痕「为什么在这档」；旧画像给理由与命中路 */}
											{String(c.tier_why ?? c.why ?? c.reason ?? "")}
											{Array.isArray(c.routes) && c.routes.length
												? ` · 命中 ${(c.routes as string[]).join("、")}`
												: ""}
											{c.intent_tag ? " · 意图标签命中" : ""}
										</span>
									</span>,
								])}
							/>
						) : (
							<Muted>没有候选工具（交 Agent 决定追问或说明）。</Muted>
						)}
						{blocked.length ? (
							<div className="text-xs text-text-muted">
								准入拦截（安全约束）：
								{blocked
									.map((b) => `${String(b.tool_id)}（${String(b.reason ?? b.state ?? "")}）`)
									.join("；")}
							</div>
						) : null}
						<GuideTrace trace={d.guide_trace} />
					</div>
				);
			})}
			<div className="text-xs text-text-secondary">
				召回的指南：{guides.length ? guides.join("、") : "无"}
			</div>
			{notes.length ? <div className="text-xs text-text-muted">{notes.join("；")}</div> : null}
		</div>
	);
}

/** 指南进来 ／ 没进来的原因（画像 pv5 起，标签体系 v2 批 4）：进来的列相关度、加分、最终分；没进来的列准入 ／ 系统事实 ／ 分数不够 */
function GuideTrace({ trace }: { trace: unknown }) {
	if (!trace || typeof trace !== "object") return null;
	const t = trace as { judged?: boolean; in?: unknown[]; out?: unknown[] };
	const rows = (xs: unknown[] | undefined) =>
		(Array.isArray(xs) ? xs : []) as { guide_id?: string; why?: string }[];
	const hits = rows(t.in);
	const miss = rows(t.out);
	return (
		<details className="text-xs" data-testid="plan-detail-guide-trace">
			<summary className="cursor-pointer text-text-secondary">
				指南：进来 {hits.length} 条 · 没进来 {miss.length} 条
				{t.judged ? "（相关性判断）" : "（相关性没判到，按意图标签）"}
			</summary>
			<div className="mt-1.5 space-y-1">
				<KvTable rows={hits.map((h) => [String(h.guide_id), `进来：${String(h.why ?? "")}`])} />
				<KvTable rows={miss.map((h) => [String(h.guide_id), `没进来：${String(h.why ?? "")}`])} />
			</div>
		</details>
	);
}

function SignalDetail({ ev }: { ev?: PlanEvent }) {
	if (!ev) return <Muted>本轮没有信号结果。</Muted>;
	const a = attrs(ev);
	const list = (v: unknown) => (Array.isArray(v) && v.length ? v.map(String).join("、") : "—");
	return (
		<div className="space-y-3" data-testid="plan-detail-signal">
			<KvTable
				rows={[
					["首轮", a.first_turn ? "是（首轮不判接续）" : "否"],
					["接续动作", zh(RESUME_ACTION_ZH, String(a.resume_action ?? "") || null)],
					["指向事项", String(a.target_item_id ?? "—")],
					["本轮新建", list(a.new_item_ids)],
					["合并到", mergedList(a.merged_into).join("、") || "—"],
					["确认作废", a.confirm_voided ? "是" : "否"],
					["降级", a.degraded ? String(a.degraded) : "否"],
				]}
			/>
			<div>
				<Label ai>信号诉求</Label>
				<JsonBlock value={a.signal_demands ?? []} maxHeight={200} />
			</div>
		</div>
	);
}

/* ---------------- 计划：版本对比（O-3）／ 事项清单 ／ 计划校验 ---------------- */

type PlanItemLike = Record<string, unknown>;
type CmpRow = { id: string; it: PlanItemLike; mark: string; why?: string };

function itemLine(it: PlanItemLike, demandLabel: DemandLabel): string {
	const cands = Array.isArray(it.candidates)
		? (it.candidates as unknown[])
				.map((c) =>
					typeof c === "string"
						? c
						: `${String((c as PlanItemLike).tool_id)}（${zh(CANDIDATE_ROLE_ZH, String((c as PlanItemLike).role ?? "") || null)}）`,
				)
				.join("、")
		: "";
	return [
		demandLabel(String(it.demand_type ?? "") || null),
		objectText(it.object),
		cands ? `候选 ${cands}` : "",
		activationText(it.activation as { mode?: string; turn?: number | null } | null),
	]
		.filter(Boolean)
		.join(" · ");
}

function rawItemId(i: number): string {
	return `it-${String(i + 1).padStart(2, "0")}`;
}

/** 计划版本：取 A-24 的版本记录 */
function useVersionSource(model: TraceModel, detail: PlanRecordDetail | null) {
	const vq = usePlanVersions(detail?.record.session_id ?? null);
	return useMemo(() => {
		const tree = detail?.tree ?? [];
		const ver = model.planVersion ?? 1;
		const versions = vq.data?.versions ?? [];
		if (versions.length) {
			const cur = versions.find((v) => v.version === ver) ?? (versions.at(-1) as PlanVersionRecord);
			const prev =
				versions.find((v) => v.version === (cur.prev_version ?? cur.version - 1)) ?? null;
			const turnHasPlanning = tree.some((e) => op(e) === "plan.planning");
			return { cur, prev, changedThisTurn: turnHasPlanning, loading: false, fromApi: true };
		}
		return { cur: null, prev: null, changedThisTurn: false, loading: vq.isLoading, fromApi: false };
	}, [vq.data, vq.isLoading, detail, model.planVersion]);
}

function PlanCompare({ model, detail }: { model: TraceModel; detail: PlanRecordDetail | null }) {
	const src = useVersionSource(model, detail);
	if (src.loading) return <LoadingInline />;
	if (!src.cur)
		return (
			<Muted>
				本轮计划没变（v{model.planVersion ?? 1}）；版本对比在计划生成的那一轮（第 1 轮）看。
			</Muted>
		);
	const cur = src.cur;
	const checks = cur.checks ?? [];
	const reasonOf = (id: string) =>
		checks
			.filter((c) => c.item_id === id && c.verdict !== "pass")
			.map((c) => `${c.rule} ${zh(CHECK_VERDICT_ZH, c.verdict)}：${c.detail}`)
			.join("；");
	let leftTitle: string;
	let left: CmpRow[];
	let right: CmpRow[];
	const curItems = (cur.items ?? []) as PlanItemLike[];
	if (src.prev) {
		leftTitle = `v${src.prev.version}`;
		const prevItems = (src.prev.items ?? []) as PlanItemLike[];
		const curIds = new Set(curItems.map((i) => String(i.item_id)));
		const prevIds = prevItems.map((i) => String(i.item_id));
		left = prevItems.map((it) => ({
			id: String(it.item_id),
			it,
			mark: curIds.has(String(it.item_id)) ? "same" : "closed",
		}));
		right = curItems.map((it, i) => {
			const id = String(it.item_id);
			const old = prevItems.find((p) => String(p.item_id) === id);
			const mark = !old
				? "added"
				: JSON.stringify(old) !== JSON.stringify(it)
					? "rewritten"
					: prevIds.filter((x) => curIds.has(x)).indexOf(id) !== i
						? "reordered"
						: "same";
			return { id, it, mark };
		});
	} else {
		leftTitle = "计划器原稿";
		const raw = (cur.raw_output ?? {}) as PlanItemLike;
		const rawItems = (Array.isArray(raw.items) ? raw.items : []) as PlanItemLike[];
		const curIds = new Set(curItems.map((i) => String(i.item_id)));
		const rawIds = new Set(rawItems.map((_, i) => rawItemId(i)));
		left = rawItems.map((it, i) => {
			const id = rawItemId(i);
			const why = reasonOf(id);
			const mark = !curIds.has(id) ? "rejected" : why ? "fixed" : "same";
			return { id, it, mark, why };
		});
		right = curItems.map((it) => {
			const id = String(it.item_id);
			const why = reasonOf(id);
			return { id, it, mark: !rawIds.has(id) ? "split" : why ? "fixed" : "same", why };
		});
	}
	const trigger = cur.trigger === "first" || !cur.trigger ? "首轮" : String(cur.trigger);
	return (
		<div className="space-y-3" data-testid="plan-detail-compare">
			<div className="text-xs text-text-secondary">
				触发原因：{trigger} · 生成者 {zh(PLAN_GENERATOR_ZH, cur.generator ?? "rule")}
				{src.changedThisTurn ? "" : " · 本轮计划没变"}
			</div>
			<div className="grid grid-cols-2 gap-3">
				<CmpCol title={leftTitle} rows={left} testId="plan-compare-left" />
				<CmpCol
					title={src.prev ? `v${cur.version}` : `校验后 v${cur.version}`}
					rows={right}
					testId="plan-compare-right"
				/>
			</div>
		</div>
	);
}

function CmpCol({ title, rows, testId }: { title: string; rows: CmpRow[]; testId: string }) {
	const demandLabel = useDemandLabel();
	return (
		<div className="min-w-0 space-y-2" data-testid={testId}>
			<div className="text-xs font-medium text-text-primary">{title}</div>
			{rows.length ? (
				rows.map((r) => (
					<div
						key={r.id}
						className={cn(
							"space-y-1 rounded-lg border border-surface-border p-2 text-xs",
							r.mark === "rejected" || r.mark === "closed"
								? "bg-surface-subtle text-text-muted"
								: "",
						)}
						data-testid="plan-compare-item"
						data-mark={r.mark}
					>
						<div className="flex flex-wrap items-center gap-1.5">
							<span className="font-mono">{r.id}</span>
							<Badge tone={PLAN_CHANGE_TONE[r.mark] ?? "muted"}>{zh(PLAN_CHANGE_ZH, r.mark)}</Badge>
						</div>
						<div className="text-text-secondary">{itemLine(r.it, demandLabel)}</div>
						{r.why ? <div className="text-brand-warning">{r.why}</div> : null}
					</div>
				))
			) : (
				<div className="text-xs text-text-muted">（空）</div>
			)}
		</div>
	);
}

function ItemList({ items }: { items: PlanItemView[] }) {
	const demandLabel = useDemandLabel();
	if (!items.length) return <Muted>本轮末没有事项（计划为空）。</Muted>;
	const cols: PlanColumn<PlanItemView>[] = [
		{ key: "id", title: "事项", className: "font-mono", render: (i) => i.item_id },
		{ key: "d", title: "诉求", render: (i) => demandLabel(i.demand_type) },
		{ key: "o", title: "绑定对象", render: (i) => objectText(i.object) || "—" },
		{
			key: "s",
			title: "状态",
			render: (i) =>
				i.active ? (
					`${zh(ITEM_STATUS_ZH, i.status)}${i.wait_kind ? `（${zh(WAIT_KIND_ZH, i.wait_kind)}）` : ""}`
				) : (
					<span className="text-text-muted">未激活 · {activationText(i.activation)}</span>
				),
		},
		{
			key: "e",
			title: "证据",
			render: (i) =>
				i.evidence_brief.filter(Boolean).length ? i.evidence_brief.filter(Boolean).join("、") : "—",
		},
	];
	return <PlanTable columns={cols} rows={items} rowKey={(i) => i.item_id} density="compact" />;
}

type CheckLike = { rule?: unknown; item_id?: unknown; verdict?: unknown; detail?: unknown };

function ChecksTable({ checks }: { checks: (PlanEvent | CheckLike)[] }) {
	const rows = checks.map((c, i) => {
		const a = ("attributes" in c ? (c as PlanEvent).attributes : c) as CheckLike;
		return {
			k: `${i}`,
			rule: String(a.rule ?? ""),
			item: String(a.item_id ?? "—"),
			verdict: String(a.verdict ?? ""),
			detail: String(a.detail ?? ""),
		};
	});
	if (!rows.length) return <Muted>本轮没有计划校验。</Muted>;
	const cols: PlanColumn<(typeof rows)[number]>[] = [
		{ key: "r", title: "规则", className: "font-mono", render: (r) => r.rule },
		{ key: "i", title: "事项", className: "font-mono", render: (r) => r.item },
		{
			key: "v",
			title: "结果",
			render: (r) => (
				<Badge
					tone={r.verdict === "pass" ? "muted" : r.verdict === "reject" ? "danger" : "warning"}
				>
					{zh(CHECK_VERDICT_ZH, r.verdict)}
				</Badge>
			),
		},
		{ key: "d", title: "说明", className: "whitespace-normal", render: (r) => r.detail },
	];
	return (
		<div data-testid="plan-detail-checks">
			<PlanTable columns={cols} rows={rows} rowKey={(r) => r.k} density="compact" />
		</div>
	);
}

/* ---------------- 事项 ／ 候选表（O-2） ---------------- */

function ItemDetail({ g, tree }: { g: TraceGroup; tree: PlanEvent[] }) {
	const demandLabel = useDemandLabel();
	const it = g.item;
	if (!it) return <Muted>没有事项信息。</Muted>;
	const reason = it.status_reason as { text?: unknown; code?: unknown } | null;
	const asked = tree.find(
		(e) =>
			op(e) === "plan.item_event" &&
			e["plan.item_id"] === it.item_id &&
			attrs(e).event_kind === "question_asked",
	);
	return (
		<KvTable
			testId="plan-detail-item"
			rows={[
				[
					"事项",
					<span key="id" className="font-mono">
						{it.item_id}
					</span>,
				],
				["诉求", demandLabel(it.demand_type)],
				["绑定对象", objectText(it.object) || "—"],
				[
					"状态",
					it.active
						? `${zh(ITEM_STATUS_ZH, it.status)}${it.wait_kind ? `（${zh(WAIT_KIND_ZH, it.wait_kind)}）` : ""}`
						: `未激活（${activationText(it.activation)}）`,
				],
				["状态原因", reason?.text ? String(reason.text) : "—"],
				["证据", it.evidence_brief.filter(Boolean).join("、") || "—"],
				[
					"激活",
					`${activationText(it.activation)}${it.activated_turn ? ` · 第 ${it.activated_turn} 轮激活` : ""}`,
				],
				["本轮步数", `${g.steps ?? 0} 步（预算见配置）`],
				[
					"问客户",
					asked
						? zh(
								WAIT_KIND_ZH,
								String(
									(attrs(asked).pending_question as { kind?: unknown } | undefined)?.kind ?? "",
								) || null,
							)
						: "—",
				],
			]}
		/>
	);
}

type CandRow = {
	tool: string;
	source: string;
	tier: string;
	rank: string;
	reason: string;
	role: string | null;
	called: boolean;
};

function CandidateTable({
	itemId,
	model,
	detail,
}: {
	itemId: string;
	model: TraceModel;
	detail: PlanRecordDetail | null;
}) {
	const src = useVersionSource(model, detail);
	const tree = detail?.tree ?? [];
	if (src.loading) return <LoadingInline />;
	const raw = (src.cur?.raw_output ?? null) as PlanItemLike | null;
	const rawItems = (Array.isArray(raw?.items) ? raw?.items : []) as PlanItemLike[];
	const idx = rawItems.findIndex((_, i) => rawItemId(i) === itemId);
	if (!raw || idx < 0)
		return (
			<Muted>本轮看不到这件事的候选：候选在建这件事项的那一轮给出，到那一轮的记录里看。</Muted>
		);
	const rawItem = rawItems[idx] as PlanItemLike;
	const pack = (raw.recall_pack ?? {}) as PlanItemLike;
	const demands = (Array.isArray(pack.demands) ? pack.demands : []) as PlanItemLike[];
	const demand = demands.find((d) => d.demand_id === rawItem.demand_ref);
	const bound = (Array.isArray(rawItem.candidates) ? rawItem.candidates : []) as PlanItemLike[];
	const calledTools = new Set(
		tree
			.filter((e) => op(e) === "execute_tool" && e["plan.item_id"] === itemId)
			.map((e) => String(attrs(e).tool_id)),
	);
	const seen = new Set<string>();
	const rows: CandRow[] = [];
	for (const c of (Array.isArray(demand?.candidates) ? demand?.candidates : []) as PlanItemLike[]) {
		const tool = String(c.tool_id);
		seen.add(tool);
		const b = bound.find((x) => x.tool_id === tool);
		rows.push({
			tool,
			source: String(pack.source ?? "recall") === "recall" ? "召回" : String(pack.source),
			tier: String(c.tier ?? "—"),
			rank: c.rank === undefined ? "—" : String(c.rank),
			reason: String(c.reason_text ?? c.reason_code ?? "—"),
			role: b ? String(b.role ?? "") : null,
			called: calledTools.has(tool),
		});
	}
	for (const b of bound) {
		const tool = String(b.tool_id);
		if (seen.has(tool)) continue;
		rows.push({
			tool,
			source: "事项直给",
			tier: "—",
			rank: "—",
			reason: "—",
			role: String(b.role ?? ""),
			called: calledTools.has(tool),
		});
	}
	const cols: PlanColumn<CandRow>[] = [
		{ key: "t", title: "对象", className: "font-mono", render: (r) => r.tool },
		{ key: "s", title: "召回来源", render: (r) => r.source },
		{ key: "a", title: "准入", render: () => <span className="text-text-muted">P1 起提供</span> },
		{ key: "tier", title: "档位", render: (r) => r.tier },
		{ key: "rank", title: "名次", align: "right", render: (r) => r.rank },
		{ key: "why", title: "理由", render: (r) => r.reason },
		{
			key: "role",
			title: "进计划",
			render: (r) =>
				r.role ? (
					<Badge tone="success">选中 · {zh(CANDIDATE_ROLE_ZH, r.role || null)}</Badge>
				) : (
					<span className="text-text-muted" title="召回了但没绑定到本事项">
						未选中
					</span>
				),
		},
		{
			key: "c",
			title: "本轮调用",
			render: (r) => (r.called ? <Badge tone="info">调了</Badge> : "—"),
		},
	];
	return (
		<div className="space-y-2" data-testid="plan-detail-candidates">
			<div className="text-xs text-text-secondary">
				诉求 {String(demand?.intent ?? rawItem.demand_type ?? "—")} · 置信{" "}
				{String(demand?.confidence_tier ?? "—")} · 结论 {String(demand?.conclusion ?? "—")}
				（选中的和没选中的都列）
			</div>
			<PlanTable
				columns={cols}
				rows={rows}
				rowKey={(r) => r.tool}
				density="compact"
				rowTestId="plan-candidate-row"
			/>
		</div>
	);
}

/* ---------------- 护栏 ／ 判停 ---------------- */

function GuardDetail({ ev }: { ev: PlanEvent }) {
	const a = attrs(ev);
	const id = String(a.guard_id ?? "");
	const v = String(a.verdict ?? "");
	const rest = Object.entries(a).filter(([k]) => !["guard_id", "verdict"].includes(k));
	return (
		<div className="space-y-3" data-testid="plan-detail-guard">
			<div className="flex flex-wrap items-center gap-1.5 text-sm">
				<span className="font-mono text-text-primary">{id}</span>
				<span className="text-text-secondary">{GUARD_ZH[id] ?? ""}</span>
				<Badge tone={GUARD_VERDICT_TONE[v] ?? "warning"}>{zh(GUARD_VERDICT_ZH, v)}</Badge>
				{ev["plan.item_id"] ? <Badge tone="info">属于 {ev["plan.item_id"]}</Badge> : null}
			</div>
			{rest.length ? (
				<KvTable
					rows={rest.map(([k, val]) => [
						k,
						typeof val === "object" && val !== null ? (
							<span key={k} className="font-mono">
								{JSON.stringify(val)}
							</span>
						) : (
							String(val)
						),
					])}
				/>
			) : (
				<Muted>没有更多明细。</Muted>
			)}
		</div>
	);
}

function StopDetail({ ev, tree }: { ev: PlanEvent; tree: PlanEvent[] }) {
	const a = attrs(ev);
	const iid = ev["plan.item_id"];
	const changes = tree.filter(
		(e) =>
			op(e) === "plan.item_event" &&
			e["plan.item_id"] === iid &&
			attrs(e).event_kind === "state_change",
	);
	return (
		<div className="space-y-3" data-testid="plan-detail-stop">
			<KvTable
				rows={[
					["判停", zh(ITEM_STATUS_ZH, String(a.stop ?? "") || null)],
					["等什么", a.wait_kind ? zh(WAIT_KIND_ZH, String(a.wait_kind)) : "—"],
					["证据步", a.evidence_step === undefined ? "—" : String(a.evidence_step)],
					["属于事项", String(iid ?? "—")],
				]}
			/>
			<div>
				<Label>本事项状态变化（本轮）</Label>
				{changes.length ? (
					<div className="space-y-1 text-xs text-text-secondary">
						{changes.map((c) => (
							<div key={c.span_id}>
								{zh(ITEM_STATUS_ZH, String(attrs(c).from ?? ""))} →{" "}
								{zh(ITEM_STATUS_ZH, String(attrs(c).to ?? ""))}
								{(attrs(c).status_reason as { text?: unknown } | null)?.text
									? ` · ${String((attrs(c).status_reason as { text?: unknown }).text)}`
									: ""}
							</div>
						))}
					</div>
				) : (
					<Muted>本轮没有状态变化。</Muted>
				)}
			</div>
		</div>
	);
}

/* ---------------- 生成：调用了 vs 用进答案（O-4）／ 旁检 ---------------- */

function CalledVsUsed({ tree, detail }: { tree: PlanEvent[]; detail: PlanRecordDetail | null }) {
	const tools = tree.filter((e) => op(e) === "execute_tool");
	const n3 = tree.find((e) => op(e) === "chat" && chatNode(e) === "n3_generate");
	const used = detail?.used_in_answer;
	return (
		<div className="space-y-3" data-testid="plan-detail-called">
			<div className="grid grid-cols-2 gap-3">
				<div className="min-w-0 space-y-1.5" data-testid="plan-called-col">
					<div className="text-xs font-medium text-text-primary">调用了</div>
					{tools.length ? (
						tools.map((e) => {
							const a = attrs(e);
							return (
								<div
									key={e.span_id}
									className="rounded-lg border border-surface-border p-2 text-xs"
								>
									<div className="flex flex-wrap items-center gap-1.5">
										<span className="font-mono text-text-primary">{String(a.tool_id)}</span>
										<Badge tone="muted">{String(a.status ?? "—")}</Badge>
										{a.write_class ? (
											<Badge tone="warning">{zh(WRITE_CLASS_ZH, String(a.write_class))}</Badge>
										) : null}
									</div>
									<div className="text-text-muted">属于 {e["plan.item_id"] ?? "—"}</div>
								</div>
							);
						})
					) : (
						<div className="text-xs text-text-muted">本轮没调工具。</div>
					)}
					<div className="pt-1 text-xs text-text-muted">召回的指南：见「召回」组。</div>
				</div>
				<div className="min-w-0 space-y-1.5" data-testid="plan-used-col">
					<div className="text-xs font-medium text-text-primary">用进答案</div>
					{used?.available ? (
						<JsonBlock value={used} />
					) : (
						<div className="rounded-lg bg-surface-subtle p-3 text-xs text-text-muted">
							{used?.note ?? "P1 起提供"}
						</div>
					)}
				</div>
			</div>
			{n3 ? (
				<Muted>
					N3 回复类型：
					{zh(
						REPLY_TYPE_ZH,
						String(
							parseJson((attrs(n3).output as { raw?: unknown } | undefined)?.raw)?.reply_type ?? "",
						) || null,
					)}
				</Muted>
			) : null}
		</div>
	);
}

function SideDetail({ tree }: { tree: PlanEvent[] }) {
	const side = tree.find((e) => op(e) === "plan.side_check");
	const speak = tree.find((e) => op(e) === "plan.speakable");
	const results = (Array.isArray(attrs(side).results) ? attrs(side).results : []) as Record<
		string,
		unknown
	>[];
	const items = (Array.isArray(attrs(speak).items) ? attrs(speak).items : []) as Record<
		string,
		unknown
	>[];
	return (
		<div className="space-y-3" data-testid="plan-detail-side">
			<div className="text-xs text-text-secondary">
				旁检 MVP-0 只告警、不重写；告警 {side ? sideCheckWarns(attrs(side)) : 0} 件。
			</div>
			{results.length ? (
				<KvTable
					rows={results.map((r) => [
						String(r.item_id),
						r.warn ? (
							<span key={String(r.item_id)} className="text-brand-warning">
								告警：{Array.isArray(r.sentences) ? r.sentences.join("；") : ""}
							</span>
						) : (
							"通过"
						),
					])}
				/>
			) : (
				<Muted>本轮没有需要旁检的写操作事项。</Muted>
			)}
			<div>
				<Label>可说口径（N3 前）</Label>
				{items.length ? (
					<KvTable
						rows={items.map((i) => [
							String(i.item_id),
							`${String(i.speakable)} · ${String(i.text ?? "")}`,
						])}
					/>
				) : (
					<Muted>—</Muted>
				)}
			</div>
		</div>
	);
}
